import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { fetchBySlugOrId, unwrapData } from "../lib/catalog";
import CatalogImage from "../components/common/CatalogImage";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";

function ListBlock({ title, items, emptyText }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <h5 className="mb-3">{title}</h5>
        {Array.isArray(items) && items.length > 0 ? (
          <ul className="mb-0 ps-3">
            {items.map((item) => (
              <li key={typeof item === "string" ? item : JSON.stringify(item)}>{typeof item === "string" ? item : item?.name || item?.title || String(item)}</li>
            ))}
          </ul>
        ) : (
          <div className="text-secondary small">{emptyText}</div>
        )}
      </div>
    </div>
  );
}

export default function HotelDetailPage() {
  const { id } = useParams();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function loadHotel() {
      try {
        const payload = await fetchBySlugOrId(
          (path) => agentApi.get(path),
          id,
          "/umrah-hotels/slug",
          "/umrah-hotels/id"
        );
        if (!alive) return;
        const data = unwrapData(payload);
        setHotel(data.hotel || data);
      } catch (fetchError) {
        if (!alive) return;
        showApiError(fetchError, "LOAD_HOTEL");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadHotel();

    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Hotel detail"
          title={hotel?.name || "Live hotel"}
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Hotels", href: "/hotels" },
            { label: hotel?.name || "Hotel Details" },
          ]}
          backTo={true}
        />

        {loading ? <PageLoader message="Loading hotel details..." /> : null}

        {!loading && hotel ? (
          <>
            <div className="row g-4 mb-4">
              <div className="col-12 col-xl-5">
                <div className="card border-0 shadow-sm h-100 catalog-card">
                  <CatalogImage folder="umrah_hotel_list" fileName={hotel.image} alt={hotel.name} height={320} />
                  <div className="card-body">
                    <div className="d-flex justify-content-between gap-3 mb-2">
                      <StatusBadge value={hotel.status || "Unknown"} />
                      <span className="text-secondary small">{hotel.code || "No code"}</span>
                    </div>
                    <h4 className="mb-3">{hotel.name}</h4>
                    <div className="row g-3 small">
                      <div className="col-6"><strong>Stars</strong><br />{hotel.stars || "Not set"}</div>
                      <div className="col-6"><strong>Distance</strong><br />{hotel.formattedDistance || hotel.distance || "Not set"}</div>
                      <div className="col-6"><strong>Check in</strong><br />{hotel.checkIn || "Not set"}</div>
                      <div className="col-6"><strong>Check out</strong><br />{hotel.checkOut || "Not set"}</div>
                      <div className="col-12"><strong>Property type</strong><br />{hotel.propertyType || "Not set"}</div>
                      <div className="col-12"><strong>Address</strong><br />{hotel.address || "Not set"}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-7">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <h5 className="mb-3">Overview</h5>
                    <div className="small mb-4" dangerouslySetInnerHTML={{ __html: hotel.description || "No description returned by the current API." }} />
                    <div className="row g-3 small text-secondary">
                      <div className="col-md-6"><strong>Contact person:</strong> {hotel.contactPerson || "Not provided"}</div>
                      <div className="col-md-6"><strong>Mobile:</strong> {hotel.mobile || hotel.contactNo || "Not provided"}</div>
                      <div className="col-md-6"><strong>Email:</strong> {hotel.email || "Not provided"}</div>
                      <div className="col-md-6"><strong>Website:</strong> {hotel.website || "Not provided"}</div>
                    </div>
                    <hr className="my-4" />
                    <div className="small">
                      <div className="mb-2"><strong>Notes</strong><br />{hotel.notes || "Not provided"}</div>
                      <div className="mb-2"><strong>Cancellation</strong><br />{hotel.cancellationPolicy || "Not provided"}</div>
                      <div><strong>Terms</strong><br />{hotel.terms || "Not provided"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-12 col-lg-4">
                <ListBlock title="Facilities" items={hotel.facilities} emptyText="No facilities returned by the current API." />
              </div>
              <div className="col-12 col-lg-4">
                <ListBlock title="Includes" items={hotel.includes} emptyText="No includes returned by the current API." />
              </div>
              <div className="col-12 col-lg-4">
                <ListBlock title="Excludes" items={hotel.excludes} emptyText="No excludes returned by the current API." />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
